// src/pages/ForgotPasswordPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import './ForgotPasswordPage.css';
import GateIcon from '../components/GateIcon';
import SpeechIcon from "../components/SpeechIcon";
import OfflineBanner from '../components/OfflineBanner';


function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  const navigate = useNavigate();

  // 🔌 Offline-/Online-Status beobachten (für Banner & Logik)
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) return;

    // 🚫 Wenn offline → gar nicht erst versuchen
    if (!isOnline) {
      setError(
        'Du scheinst offline zu sein. Der Passwort-Reset-Link kann nur mit Internetverbindung gesendet werden.'
      );
      return;
    }

    // 🔐 Sicherheitsnetz: falls auth aus irgendeinem Grund nicht initialisiert ist
    if (!auth) {
      setError(
        'Es ist ein technisches Problem mit der Anmeldung aufgetreten. Bitte versuche es später erneut.'
      );
      return;
    }

    try {
      setIsSending(true);
      await sendPasswordResetEmail(auth, email.trim());
      setEmailSent(true);
    } catch (err) {
      console.error('Fehler beim Passwort-Reset:', err);

      if (err.code === 'auth/user-not-found') {
        setError('Unter dieser E-Mail ist kein Konto registriert.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Bitte gib eine gültige E-Mail-Adresse ein.');
      } else if (err.code === 'auth/network-request-failed') {
        setError(
          'Es gab ein Netzwerkproblem. Bitte überprüfe deine Internetverbindung und versuche es erneut.'
        );
      } else {
        setError(
          'Passwort-Reset konnte nicht durchgeführt werden. Bitte später erneut versuchen.'
        );
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="ForgotPasswordPage">
      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Edens Ernte Logo"
        className="browse-logo"
        onClick={() => navigate('/')}
      />
      <GateIcon className="login-icon" />
      <SpeechIcon className="speech-icon no-tap-highlight" />
      <img
        src="/rezept-icon.png"
        alt="Rezepthelfer Icon"
        className="rezept-icon"
        onClick={() => navigate('/rezepte')}
      />

      <p className="top-claim">Nachhaltigkeit – ganz einfach!</p>

      {/* 🔌 Offline-Banner */}
      {!isOnline && <OfflineBanner onRetry={() => setIsOnline(navigator.onLine)} />}

      <div className="forgot-content">
        {emailSent ? (
          <>
            <h2>Überprüfe deine E-Mail!</h2>
            <p>
              Wir haben dir eine E-Mail zum Zurücksetzen deines Passworts
              geschickt, bitte schaue auch im Spam Ordner nach!
            </p>
          </>
        ) : (
          <>
            <h2>Passwort vergessen?</h2>
            <p>
              Bitte gib deine E-Mail-Adresse ein, um dein Passwort
              zurückzusetzen.
            </p>
            <form onSubmit={handleSubmit} className="forgot-form">
              <input
                type="email"
                placeholder="Deine E-Mail-Adresse"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button
                type="submit"
                className="green-button"
                disabled={isSending}
              >
                {isSending ? 'Sende Link…' : 'Link senden'}
              </button>
            </form>
            {error && <p className="error-text">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
}

export default ForgotPasswordPage;